import React, { useState, useContext } from "react";
import axios from "../../axiosConfig";
import "../../styles/TrainerNotes.css";
import { AiOutlineDelete, AiOutlineEdit } from "react-icons/ai";
import TrainerNoteModal from "./TrainerNoteModal";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import { TrainerContext } from "../../context/TrainerContext";
import {useToast} from "../commons/toast/ToastContext"; // ✅ Get trainer details

const TrainerNotesList = ({ notes, dogId }) => {
    const { trainer } = useContext(TrainerContext); // ✅ Get trainer from context
    const [noteList, setNoteList] = useState(Array.isArray(notes) ? notes : []);
    const [error, setError] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editNoteId, setEditNoteId] = useState(null);
    const [initialContent, setInitialContent] = useState("");
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [noteToDelete, setNoteToDelete] = useState(null);

    // Initialize toast hook
    const toast = useToast();

    const trainerName = trainer?.firstName || "Trener"; // ✅ Get trainer name safely

    // ✅ Open Modal for Add or Edit
    const openModal = (note = null) => {
        if (note) {
            setEditNoteId(note.id);
            setInitialContent(note.content);
        } else {
            setEditNoteId(null);
            setInitialContent("");
        }
        setIsModalOpen(true);
    };

    // ✅ Add or Edit Note
    const saveNote = async (content) => {
        if (!content.trim()) {
            toast.warning("Notatka nie może być pusta", "Brakujące dane");
            return;
        }

        try {
            const token = localStorage.getItem("jwt");

            if (editNoteId) {
                // Edit Note
                const response = await axios.put(`/api/v1/trainer-note/edit/${editNoteId}`, {
                    dogId,
                    trainerName,
                    content,
                }, { headers: { Authorization: `Bearer ${token}` } });

                setNoteList(noteList.map(n => n.id === editNoteId ? response.data : n));
                toast.success("Notatka została zaktualizowana", "Sukces");
            } else {
                // Add Note
                const response = await axios.post("/api/v1/trainer-note/add", {
                    dogId,
                    trainerName,
                    content,
                }, { headers: { Authorization: `Bearer ${token}` } });

                setNoteList([response.data, ...noteList]);
                toast.success("Notatka została dodana", "Sukces");
            }

            setIsModalOpen(false);
            setError(""); // Clear any previous errors
        } catch (err) {
            const errorMessage = editNoteId
                ? "Nie udało się zaktualizować notatki"
                : "Nie udało się zapisać notatki";

            setError(errorMessage);
            toast.error(errorMessage, "Błąd");
        }
    };

    // ✅ Delete Note with Confirmation
    const deleteNote = async () => {
        if (!noteToDelete) return;

        try {
            const token = localStorage.getItem("jwt");
            await axios.delete(`/api/v1/trainer-note/${noteToDelete}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setNoteList(noteList.filter(n => n.id !== noteToDelete));
            setIsDeleteModalOpen(false);
            setError(""); // Clear any previous errors
            toast.success("Notatka została usunięta", "Sukces");
        } catch (err) {
            const errorMessage = "Nie udało się usunąć notatki";
            setError(errorMessage);
            toast.error(errorMessage, "Błąd");
        }
    };

    if (!noteList || noteList.length === 0) {
        return (
            <div className="trainer-notes-container">
                <div className="add-note-container">
                    <button className="add-note-btn" onClick={() => openModal()}>Dodaj notatkę</button>
                </div>

                {/* ✅ Reusable Add/Edit Note Modal */}
                <TrainerNoteModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSave={saveNote}
                    initialContent={initialContent}
                    isEditing={!!editNoteId}
                />

                {error && <p className="error">{error}</p>}

                <p>Brak dostępnych notatek.</p>
            </div>
        );
    }

    return (
        <div className="trainer-notes-container">
            <div className="add-note-container">
                <button className="add-note-btn" onClick={() => openModal()}>Dodaj notatkę</button>
            </div>

            {/* ✅ Reusable Add/Edit Note Modal */}
            <TrainerNoteModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={saveNote}
                initialContent={initialContent}
                isEditing={!!editNoteId}
            />

            {/* ✅ Delete Confirmation Modal */}
            <DeleteConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={deleteNote}
                itemName="notatkę"
            />

            {error && <p className="error">{error}</p>}

            {/* ✅ Notes List - matching TrainingSummaryList structure */}
            <div className="notes-list">
                {noteList.map((note) => (
                    <div key={note.id} className="note-item">
                        {/* Note Header */}
                        <div className="note-header">
                            <div className="note-header-left">
                                <p className="note-date">{new Date(note.createTime).toLocaleDateString()}</p>
                                <p className="note-trainer">{note.trainerName}</p>
                            </div>

                            {/* Note Actions */}
                            <div className="note-actions">
                                <AiOutlineEdit
                                    className="trainer-notes-edit-icon"
                                    onClick={() => openModal(note)}
                                    title="Edytuj notatkę"
                                />
                                <AiOutlineDelete
                                    className="trainer-notes-delete-icon"
                                    onClick={() => {
                                        setNoteToDelete(note.id);
                                        setIsDeleteModalOpen(true);
                                    }}
                                    title="Usuń notatkę"
                                />
                            </div>
                        </div>

                        {/* Note Content */}
                        <div className="note-content">
                            <div className="note-section">
                                <p className="section-title">Notatka</p>
                                <p className="section-text">{note.content}</p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default TrainerNotesList;